import _ from 'lodash';

import { createApiReducer } from 'src/api';

import dayjs from 'dayjs-ext';
import {
  UPDATE_ACCOUNT_PAYMENT_START,
  UPDATE_ACCOUNT_PAYMENT_SUCCESS,
  UPDATE_ACCOUNT_PAYMENT_FAILURE,
  ADD_PAYMENT_PRICE_START,
  ADD_PAYMENT_PRICE_SUCCESS,
  ADD_PAYMENT_PRICE_FAILURE,
  UPDATE_PAYMENT_PRICE_START,
  UPDATE_PAYMENT_PRICE_SUCCESS,
  UPDATE_PAYMENT_PRICE_FAILURE,
  DELETE_PAYMENT_PRICE_START,
  DELETE_PAYMENT_PRICE_SUCCESS,
  DELETE_PAYMENT_PRICE_FAILURE,
} from './actions';

export const transformPayment = response => ({
  // new payment info
  pricingFormat: response.account_pricing_format,
  processingType: response.account_processing_type,
  pinBasedDebitRate: response.account_pin_based_debit_rate,
  chargeBackFee: response.account_chargeback_fee,
  salesTrxFee: response.account_sales_trx_fee,
  monthlyMinimumFee: response.account_monthly_minimum_fee,
  pciMonthlyProgramFee: response.account_pci_monthly_program_fee,
  retrievalFee: response.account_retrieval_fee,
  returnFee: response.account_return_fee,
  batchCloseFee: response.account_batch_close_fee,
  terminalIdFee: response.account_terminal_id_fee,
  monthlyStatementFee: response.account_monthly_statement_fee,
  onlinePortalFee: response.account_online_portal_fee,
  pciNonComplianceFee: response.account_pci_non_compliance_fee,
  achRejectFee: response.account_ach_reject_fee,
  // bank information tab
  accountBankName: response.account_settlement_account_bank_name,
  accountNameOnAccount:
    response.account_settlement_account_business_name_on_checks,
  accountRoutingNumber: response.account_settlement_account_routing_number,
  accountAccountNumber: response.account_settlement_account_account_number,
  accountBankAddress: response.account_settlement_account_bank_address,
  accountBankPhoneNumber: response.account_settlement_account_bank_phone_number,
  // card processing information
  acceptedCardBefore: response.account_accepted_card_before,
  processorName: response.account_processor_name,
  priorTerminations: response.account_prior_terminations,
  priorTerminationsExplanation: response.account_prior_terminations_explanation,
  numberOfLocations: response.account_number_of_locations,
  existingMid: response.account_existing_mid,
  annualVolume: response.account_annual_volume,
  monthlyVolume: response.account_monthly_volume,
  avgTicket: response.account_avg_ticket,
  highTicket: response.account_high_ticket,
  processingStartDate: response.account_estimated_processing_start_date
    ? dayjs(response.account_estimated_processing_start_date)
        .utc()
        .toDate()
    : null,
  // equipment
  terminalModel1: response.account_terminal_model_1,
  terminalModel2: response.account_terminal_model_2,
  terminalModel3: response.account_terminal_model_3,
  terminalQuantity1: response.account_terminal_qty_1,
  terminalQuantity2: response.account_terminal_qty_2,
  terminalQuantity3: response.account_terminal_qty_3,
  terminalVersion1: response.account_terminal_version_1,
  terminalVersion2: response.account_terminal_version_2,
  terminalVersion3: response.account_terminal_version_3,
  terminalPrice1: response.account_terminal_price_1,
  terminalPrice2: response.account_terminal_price_2,
  terminalPrice3: response.account_terminal_price_3,
});

export const transformPaymentPrice = response => ({
  id: response.id,
  paymentProductId: response.payment_price_payment_product.id,
  paymentProductName:
    response.payment_price_payment_product.payment_product_name,
  paymentProductIsVMD:
    response.payment_price_payment_product.payment_product_is_vmd,
  paymentProductIsAMEX:
    response.payment_price_payment_product.payment_product_is_amex,
  paymentProductIsCardService:
    response.payment_price_payment_product.payment_product_is_card_services,
  fixedFee: response.payment_price_fixed_fee,
  percentageFee: response.payment_price_percentage_fee,
});

const accountPaymentUpdateReducer = createApiReducer(
  [
    UPDATE_ACCOUNT_PAYMENT_START,
    UPDATE_ACCOUNT_PAYMENT_SUCCESS,
    UPDATE_ACCOUNT_PAYMENT_FAILURE,
  ],
  (payload, state) => {
    const newPayment = transformPayment(payload);

    return {
      ...state.data,
      payment: newPayment,
    };
  },
  error => transformPayment(_.get(error, 'payload.response', {}))
);

const updateItem = (list, key, value, newItem) => {
  return list.map(item => {
    if (item[key] === value) {
      return newItem;
    }
    return item;
  });
};

const accountAddPaymentPriceReducer = createApiReducer(
  [
    ADD_PAYMENT_PRICE_START,
    ADD_PAYMENT_PRICE_SUCCESS,
    ADD_PAYMENT_PRICE_FAILURE,
  ],
  (payload, state) => {
    const oldPaymentPrices = state.data.paymentPrices || [];
    const newPaymentPrices = payload.map(transformPaymentPrice);

    return {
      ...state.data,
      paymentPrices: [...oldPaymentPrices, ...newPaymentPrices],
    };
  },
  error => transformPaymentPrice(_.get(error, 'payload.response', {}))
);

const updatePaymentPrices = (payload, state) => {
  const newPaymentPrice = transformPaymentPrice(payload);

  return {
    ...state.data,
    paymentPrices: updateItem(
      state.data.paymentPrices,
      'id',
      newPaymentPrice.id,
      newPaymentPrice
    ),
  };
};

const accountUpdatePaymentPriceReducer = createApiReducer(
  [
    UPDATE_PAYMENT_PRICE_START,
    UPDATE_PAYMENT_PRICE_SUCCESS,
    UPDATE_PAYMENT_PRICE_FAILURE,
  ],
  updatePaymentPrices,
  error => transformPaymentPrice(_.get(error, 'payload.response', {}))
);

const accountDeletePaymentPriceReducer = createApiReducer(
  [
    DELETE_PAYMENT_PRICE_START,
    DELETE_PAYMENT_PRICE_SUCCESS,
    DELETE_PAYMENT_PRICE_FAILURE,
  ],
  (payload, state) => {
    return {
      ...state.data,
      paymentPrices: state.data.paymentPrices.filter(l => l.id !== payload.id),
    };
  },
  error => transformPaymentPrice(_.get(error, 'payload.response', {}))
);

export const reducers = [
  accountPaymentUpdateReducer,
  accountAddPaymentPriceReducer,
  accountUpdatePaymentPriceReducer,
  accountDeletePaymentPriceReducer,
];
